<template>
  <div class="shared-cv">
    <div class="shared-cv__inner">
      <p v-if="loading" class="shared-cv__msg">Loading…</p>
      <p v-else-if="error" class="shared-cv__msg">{{ error }}</p>

      <template v-else-if="candidate">
        <CvDocument
          :active="candidate"
          :share-url="pageUrl"
          @request="showRequest = true"
        />
        <p class="shared-cv__note">
          This profile was prepared for a specific enquiry. The link is private — please do not publish it.
        </p>
      </template>
    </div>

    <ModalContact
      v-if="showRequest"
      :startProjectModal="true"
      :hideProjectScope="true"
      :formMessage="requestMessage"
      :formUrl="pageUrl"
      title="Request a candidate"
      @close="showRequest = false"
    />
  </div>
</template>

<script>
/**
 * A tailored resume opened by its own link: /cv/<token>.
 *
 * The same document the site shows, minus the section around it. Views are
 * recorded against this version, so every variant sent to a client has its own
 * numbers. The page is kept out of search — it is private by intent, not by
 * secrecy, and a shared link would otherwise end up indexed.
 */
import CvDocument from "@/components/recruitment/CvDocument.vue";
import ModalContact from "@/components/ModalContact.vue";

export default {
  name: "SharedCv",
  components: { CvDocument, ModalContact },
  data() {
    return {
      candidate: null,
      loading: true,
      error: "",
      showRequest: false,
      viewId: null,
      viewStartedAt: 0,
    };
  },
  computed: {
    pageUrl() {
      return typeof window !== "undefined" ? window.location.href : "";
    },
    requestMessage() {
      if (!this.candidate) return "";
      const c = this.candidate;
      const initial = c.surname ? c.surname.trim().charAt(0).toUpperCase() + "." : "";
      // The link travels as its own field; this line says which version it is.
      return "Request for candidate " + [c.name, initial].filter(Boolean).join(" ") +
        " — " + c.stack + " (" + (c.versionLabel || "shared link") + ").";
    },
  },
  mounted() {
    this.load();
    window.addEventListener("beforeunload", this.endView);
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.endView);
    this.endView();
    this.setNoIndex(false);
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await fetch("/api/wdevs/cv/" + encodeURIComponent(this.$route.params.token));
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "HTTP " + res.status);
        this.candidate = data.candidate;
        this.setNoIndex(true);
        document.title = [this.candidate.stack, "WDEVS"].filter(Boolean).join(" — ");
        this.startView();
      } catch (e) {
        this.error = e.message;
      } finally {
        this.loading = false;
      }
    },
    /** Belt and braces: the route is also left out of the sitemap and prerender. */
    setNoIndex(on) {
      let tag = document.querySelector('meta[name="robots"][data-shared-cv]');
      if (on) {
        if (!tag) {
          tag = document.createElement("meta");
          tag.setAttribute("name", "robots");
          tag.setAttribute("data-shared-cv", "1");
          document.head.appendChild(tag);
        }
        tag.setAttribute("content", "noindex, nofollow");
      } else if (tag) {
        tag.parentNode.removeChild(tag);
      }
    },
    async startView() {
      this.viewStartedAt = Date.now();
      try {
        const res = await fetch("/api/wdevs/views", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ candidateId: this.candidate.id }),
        });
        const data = await res.json();
        this.viewId = data.viewId || null;
      } catch (e) { /* tracking is best effort */ }
    },
    endView() {
      if (!this.viewId || !this.viewStartedAt) return;
      const seconds = Math.round((Date.now() - this.viewStartedAt) / 1000);
      const body = JSON.stringify({ viewId: this.viewId, seconds });
      this.viewId = null;
      this.viewStartedAt = 0;
      if (!seconds) return;
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon("/api/wdevs/views/duration", new Blob([body], { type: "application/json" }));
        } else {
          fetch("/api/wdevs/views/duration", { method: "POST", body, keepalive: true });
        }
      } catch (e) { /* nothing else to try */ }
    },
  },
};
</script>

<style lang="scss">
@import "../scss/_variables.scss";
@import "include-media/dist/_include-media.scss";

.shared-cv {
  min-height: 100vh;
  padding: 24px 16px 60px;
  background: $gray;
  @include media(">=tablet") { padding: 40px 24px 80px; }

  &__inner {
    width: 100%;
    max-width: 880px;
    margin: 0 auto;
  }

  &__msg {
    padding: 80px 0;
    text-align: center;
    font-size: 17px;
    color: $grey;
  }

  &__note {
    margin-top: 18px;
    text-align: center;
    font-size: 13px;
    color: $grey;
  }
}
</style>
